import type { ReactNode } from 'react'
import type { ShipDto } from '@/api/hooks'
import { LauncherHullMeta, LAUNCHER_UNSEAWORTHY_MESSAGE } from '@/components/HullAlert'
import { groupModules } from '@/components/ShipModuleSlots'
import { StatIcon } from '@/components/game/GameIcons'

/** Colonne de droite des écrans de combat : les dés par phase et l'armure.
 *  C'était la colonne en dur de l'ancienne `ShipPickerList` ; elle devient une
 *  `meta` comme les autres pour que la brique serve aussi les écrans de cale. */
export function combatMeta(s: ShipDto) {
  const cs = s.combatStats
  return (
    <span className="flex items-center gap-2 flex-none">
      <span className="font-display tabular-nums text-ocean-200">
        M{cs.manoeuvre}/B{cs.bordee}/A{cs.abordage}
      </span>
      <span className="text-xs text-ocean-400 inline-flex items-center gap-0.5" title="Armure">
        <StatIcon name="armure" className="w-3 h-3" />{cs.armure}
      </span>
    </span>
  )
}

/** Colonne de droite des écrans de cargaison : la cale EFFECTIVE (modules
 *  compris, `ShipDto.cargoCapacity`). Remplace le `cale {s.cargoCapacity}` en
 *  dur des quatre copies commerce et hors-la-loi. */
export function cargoMeta(s: ShipDto) {
  return (
    <span className="flex-none text-ocean-400 inline-flex items-center gap-1 tabular-nums" title="Cale">
      <StatIcon name="cargo" className="w-3 h-3" />{s.cargoCapacity}
    </span>
  )
}

/** Formes abrégées des sept modules, pour les pastilles étroites. Le libellé
 *  complet (celui du Codex et de l'enum serveur) reste dans le `title`. */
const MODULE_ABBR: Record<string, string> = {
  long_cannons: 'Canons',
  boarding_hooks: 'Grappins',
  streamlined_keel: 'Quille',
  reinforced_hold: 'Cale',
  racing_rig: 'Gréement',
  false_bottom: 'Faux-fond',
  spyglass: 'Longue-vue',
}

/** « Aux rangs » : le mot déjà employé sur la page Flotte pour le branle-bas. */
export const RANKS_MESSAGE = 'Aux rangs'

/** Pourquoi cette coque ne peut pas être ajoutée, ou null si elle peut. */
export function blockReason(ship: ShipDto): string | null {
  if (ship.seaworthy === false) return LAUNCHER_UNSEAWORTHY_MESSAGE
  if (ship.mobilized === true) return RANKS_MESSAGE
  return null
}

function ModulePills({ ship }: { ship: ShipDto }) {
  const groups = groupModules(ship.modules ?? [])
  if (groups.length === 0) return null
  return (
    <span className="flex flex-wrap gap-1">
      {groups.map((g) => (
        <span
          key={g.type}
          title={g.label}
          className={`inline-flex items-center gap-0.5 px-1 rounded text-[10px] leading-4 border ${
            g.category === 'combat'
              ? 'text-orange-300 bg-orange-500/15 border-orange-500/30'
              : 'text-ocean-300 bg-ocean-500/15 border-ocean-500/30'
          }`}
        >
          {MODULE_ABBR[g.type] ?? g.label}
          {g.count > 1 && <span className="opacity-75">×{g.count}</span>}
        </span>
      ))}
    </span>
  )
}

export function ShipPickerRow({
  ship, checked, isLead, onToggle, meta, accentClass = 'accent-sand-300',
}: {
  ship: ShipDto
  checked: boolean
  isLead: boolean
  onToggle: (id: number) => void
  meta?: (ship: ShipDto) => ReactNode
  accentClass?: string
}) {
  const reason = blockReason(ship)
  // Une coque bloquée mais DÉJÀ cochée reste active : le blocage interdit
  // d'ajouter, jamais de retirer. Sinon elle est indécrochable.
  const disabled = reason !== null && !checked

  return (
    <label
      className={`flex items-center gap-2 px-2 py-1.5 rounded text-xs transition-colors ${
        disabled
          ? ship.mobilized
            ? 'bg-amber-500/[0.06] border border-amber-400/30 opacity-60 cursor-not-allowed'
            : 'bg-red-500/[0.08] border border-red-500/30 opacity-60 cursor-not-allowed'
          : checked
            ? 'bg-ocean-700/60 border border-sand-300/40 cursor-pointer'
            : 'bg-ocean-900/40 border border-sand-500/20 hover:border-sand-300/40 cursor-pointer'
      }`}
    >
      <input
        type="checkbox"
        checked={checked}
        disabled={disabled}
        onChange={() => onToggle(ship.id)}
        className={accentClass}
      />
      <span className="flex-1 min-w-0">
        <span className="flex items-center gap-1.5">
          {isLead && (
            <span className="px-1 rounded bg-sand-300 text-ocean-900 text-[9px] font-bold uppercase tracking-wider">
              tête
            </span>
          )}
          <strong className={reason ? (ship.mobilized ? 'text-amber-300' : 'text-red-300') : 'text-sand-300'}>
            {ship.name}
          </strong>
          {reason ? (
            <span className={`font-semibold ${ship.mobilized ? 'text-amber-300' : 'text-red-400'}`}>{reason}</span>
          ) : (
            <>
              <span className="text-ocean-300">({ship.shipTypeLabel})</span>
              <LauncherHullMeta ship={ship} />
            </>
          )}
        </span>
        <ModulePills ship={ship} />
      </span>
      {!reason && meta?.(ship)}
    </label>
  )
}
